import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBell, faBox, faUsers, faSwimmingPool, faCalendarCheck,
  faCircleInfo, faCar, faPaw, faCheckDouble, faTrash,
  faBullhorn, faTriangleExclamation, faShieldHalved
} from '@fortawesome/free-solid-svg-icons';
import { BASE_URL } from '../utils/constants';

/* ── helpers ── */
const getUserId = () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) return null;
    return JSON.parse(atob(token.split('.')[1])).id_usuario;
  } catch { return null; }
};

const getNotifRoute = () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) return '/userNotificaciones';
    const role = JSON.parse(atob(token.split('.')[1])).id_tipo_usuario;
    if (role === 1) return '/adminNotificaciones';
    if (role === 2) return '/securityNotificaciones';
    return '/userNotificaciones';
  } catch { return '/userNotificaciones'; }
};

const TIPO_META = {
  paquete:         { icon: faBox,                color: 'text-amber-600',  bg: 'bg-amber-100'  },
  visita:          { icon: faUsers,              color: 'text-blue-600',   bg: 'bg-blue-100'   },
  reserva_piscina: { icon: faSwimmingPool,       color: 'text-cyan-600',   bg: 'bg-cyan-100'   },
  reserva_salon:   { icon: faCalendarCheck,      color: 'text-violet-600', bg: 'bg-violet-100' },
  mascota:         { icon: faPaw,                color: 'text-green-600',  bg: 'bg-green-100'  },
  vehiculo:        { icon: faCar,                color: 'text-slate-600',  bg: 'bg-slate-100'  },
  sistema:         { icon: faCircleInfo,         color: 'text-indigo-600', bg: 'bg-indigo-100' },
  comunicado:      { icon: faBullhorn,           color: 'text-orange-600', bg: 'bg-orange-100' },
  incidente:       { icon: faTriangleExclamation,color: 'text-red-600',    bg: 'bg-red-100'    },
  alerta:          { icon: faShieldHalved,       color: 'text-rose-600',   bg: 'bg-rose-100'   },
  emergencia:      { icon: faTriangleExclamation,color: 'text-red-700',    bg: 'bg-red-200'    },
};

const timeAgo = (dateStr) => {
  const diff = (Date.now() - new Date(dateStr)) / 1000;
  if (diff < 60) return 'hace un momento';
  if (diff < 3600) return `hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)} h`;
  return `hace ${Math.floor(diff / 86400)} días`;
};

/**
 * NotificationBell
 * Colócalo en el navbar de cualquier rol.
 */
export default function NotificationBell() {
  const [count, setCount] = useState(0);
  const [notifs, setNotifs] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasNew, setHasNew] = useState(false);
  const [toast, setToast] = useState(null);
  const dropRef = useRef(null);
  const prevCountRef = useRef(null); // null = primera carga
  const navigate = useNavigate();
  const userId = getUserId();
  const notifRoute = getNotifRoute();

  /* Poll cada 15s: obtiene lista reciente y detecta nuevas notificaciones */
  const pollNotifs = useCallback(async () => {
    if (!userId) return;
    try {
      const r = await axios.get(`${BASE_URL}/notificaciones/user/${userId}?limit=10`);
      const data = r.data;
      const unread = data.filter(n => !n.leida).length;

      // Detectar nuevas notificaciones solo después de la primera carga
      if (prevCountRef.current !== null && unread > prevCountRef.current) {
        setHasNew(true);
        const newest = data.find(n => !n.leida);
        if (newest) {
          setToast(newest);
          setTimeout(() => setToast(null), 5000);
        }
        setTimeout(() => setHasNew(false), 2500);
      }
      prevCountRef.current = unread;

      setCount(unread);
      setNotifs(data);
    } catch { /* silencioso */ }
  }, [userId]);

  useEffect(() => {
    pollNotifs();
    const iv = setInterval(pollNotifs, 15000);
    return () => clearInterval(iv);
  }, [pollNotifs]);

  /* Cerrar dropdown al hacer click fuera */
  useEffect(() => {
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  /* Abrir/cerrar dropdown — las notificaciones ya vienen del poll */
  const toggleOpen = () => {
    if (open) { setOpen(false); return; }
    setOpen(true);
    pollNotifs(); // refresco inmediato al abrir
  };

  /* Marcar una como leída */
  const markRead = async (id) => {
    try {
      await axios.put(`${BASE_URL}/notificaciones/${id}/read`);
      setNotifs(prev => prev.map(n => n.id_notificacion === id ? { ...n, leida: 1 } : n));
      setCount(prev => Math.max(0, prev - 1));
    } catch { /* silencioso */ }
  };

  /* Marcar todas como leídas */
  const markAllRead = async () => {
    if (!userId) return;
    try {
      await axios.put(`${BASE_URL}/notificaciones/user/${userId}/read-all`);
      setNotifs(prev => prev.map(n => ({ ...n, leida: 1 })));
      setCount(0);
    } catch { /* silencioso */ }
  };

  if (!userId) return null;

  const meta = (tipo) => TIPO_META[tipo] || TIPO_META.sistema;

  return (
    <div className="relative" ref={dropRef}>
      {/* Bell button */}
      <button
        onClick={toggleOpen}
        className="relative flex items-center justify-center w-10 h-10 text-white hover:bg-white/20 rounded-lg transition-colors"
        title="Notificaciones"
      >
        <FontAwesomeIcon icon={faBell} className={`h-5 w-5 transition-transform ${hasNew ? 'animate-bounce' : ''}`} />
        {count > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1 shadow">
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-2xl shadow-2xl z-[9999] overflow-hidden border border-gray-100">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <span className="font-semibold text-gray-800 text-sm">Notificaciones</span>
            {count > 0 && (
              <button
                onClick={markAllRead}
                className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 transition-colors"
              >
                <FontAwesomeIcon icon={faCheckDouble} className="h-3 w-3" />
                Marcar todo leído
              </button>
            )}
          </div>

          {/* List */}
          <div className="max-h-80 overflow-y-auto divide-y divide-gray-50">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin h-6 w-6 rounded-full border-b-2 border-indigo-600"></div>
              </div>
            ) : notifs.length === 0 ? (
              <div className="py-10 text-center text-gray-400">
                <FontAwesomeIcon icon={faBell} className="h-8 w-8 mb-2 opacity-30" />
                <p className="text-sm">Sin notificaciones</p>
              </div>
            ) : (
              notifs.map(n => {
                const m = meta(n.tipo);
                return (
                  <div
                    key={n.id_notificacion}
                    className={`flex items-start gap-3 px-4 py-3 cursor-pointer transition-colors ${n.leida ? 'bg-white hover:bg-gray-50' : 'bg-indigo-50/60 hover:bg-indigo-50'}`}
                    onClick={() => { if (!n.leida) markRead(n.id_notificacion); }}
                  >
                    <div className={`flex-shrink-0 w-8 h-8 rounded-full ${m.bg} flex items-center justify-center mt-0.5`}>
                      <FontAwesomeIcon icon={m.icon} className={`h-3.5 w-3.5 ${m.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-1">
                        <p className={`text-xs font-semibold leading-tight ${n.leida ? 'text-gray-700' : 'text-gray-900'}`}>{n.titulo}</p>
                        {!n.leida && <span className="flex-shrink-0 w-2 h-2 bg-indigo-500 rounded-full mt-0.5"></span>}
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5 leading-relaxed line-clamp-2">{n.mensaje}</p>
                      <p className="text-[10px] text-gray-400 mt-1">{timeAgo(n.fecha_creacion)}</p>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-gray-100 px-4 py-2.5">
            <button
              onClick={() => { setOpen(false); navigate(notifRoute); }}
              className="w-full text-center text-xs text-indigo-600 hover:text-indigo-800 font-medium transition-colors"
            >
              Ver todas las notificaciones →
            </button>
          </div>
        </div>
      )}
      {/* Toast: nueva notificación entrante */}
      {toast && (
        <div className="fixed top-4 right-4 z-[10000] max-w-sm bg-white rounded-xl shadow-2xl border border-gray-100 p-4 flex items-start gap-3">
          <div className={`flex-shrink-0 w-9 h-9 rounded-full ${meta(toast.tipo).bg} flex items-center justify-center`}>
            <FontAwesomeIcon icon={meta(toast.tipo).icon} className={`h-4 w-4 ${meta(toast.tipo).color}`} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-bold text-gray-900 leading-tight">{toast.titulo}</p>
            <p className="text-xs text-gray-500 leading-relaxed line-clamp-2 mt-0.5">{toast.mensaje}</p>
          </div>
          <button onClick={() => setToast(null)} className="text-gray-400 hover:text-gray-600 text-sm leading-none flex-shrink-0 ml-1">✕</button>
        </div>
      )}
    </div>
  );
}
